/* eslint-disable react/forbid-prop-types */
import React from 'react';
import PropTypes from 'prop-types';
import { each, every, orderBy } from 'lodash';

import { ConfigsService, LocaleService } from '@sm360/widget-commons-core-services';
import {
    ErrorsHandler,
    FIELD_LENGTH,
    GroupResponseService,
    Groups,
    InputDropDown,
    InputText,
    COUNTRIES,
} from '@sm360/widget-commons-forms';

import { HOME_STATUSES } from 'financing/constants/housing.js';

import ErrorService from 'financing/services/ErrorService/ErrorService.js';
import residenceFormConfig from 'financing/components/ResidenceInfosForm/residenceFormConfig.js';

import AddressDisplay from 'financing/components/ResidenceInfosForm/AddressDisplay.jsx';
import ButtonBlock from 'financing/components/Utils/ButtonBlock.jsx';
import FormBuilder from 'financing/components/Utils/FormBuilder.jsx';

class ResidenceInfosForm extends React.Component {
    constructor(props) {
        super(props);

        this.state = {
            isSubmitted: false,
            isValid: false,
            inputs: {
                ...props.residenceInformations,
                monthCount: '',
                yearCount: '',
            },
            formValidation: {
                currentAddress: false,
                monthCount: false,
                yearCount: false,
                housingType: false,
            },
            housingType: props.residenceInformations.housingType || '',
            housingValidation: {},
            errors: [],
        };

        this.state.editModeCurrentAddress = !(this.state.address && this.state.postalCode && this.state.city);

        this.country = this.props.country || ConfigsService.getCountry();

        this.onAddressChange = this.onAddressChange.bind(this);
        this.onBack = this.onBack.bind(this);
        this.onDone = this.onDone.bind(this);
        this.onDurationChange = this.onDurationChange.bind(this);
        this.onEditAddressClick = this.onEditAddressClick.bind(this);
        this.onHousingTypeChanged = this.onHousingTypeChanged.bind(this);
        this.onHousingValueChange = this.onHousingValueChange.bind(this);
    }

    componentWillMount() {
        const { formValidation } = this.state;
        const { address } = this.state.inputs;

        const asAllAddressInfos = this.isAddressComplete(address);

        this.setState({
            editModeCurrentAddress: !asAllAddressInfos,
            formValidation: {
                ...formValidation,
                currentAddress: true,
            },
        });
    }

    componentDidMount() {
        this.provinces = orderBy(
            COUNTRIES.filter((prov) => prov.country === this.country).map((option) => {
                return {
                    value: option.code,
                    label: LocaleService.t(option.transKey),
                };
            }),
            ['value']
        );

        this.homeStatuses = HOME_STATUSES.map((status) => ({
            ...status,
            label: LocaleService.t(status.transKey),
        }));
    }

    componentWillReceiveProps(newProps) {
        const newAddress = newProps.residenceInformations.address;
        if (newAddress) {
            this.setState({
                inputs: {
                    ...this.state.inputs,
                    address: { ...newAddress },
                },
                editModeCurrentAddress: !this.isAddressComplete(newAddress),
            });
        }
    }

    onDone(callbackObject) {
        const { housingValidation, formValidation } = this.state;

        const errors = ErrorService.getErrors(formValidation, 'FINANCING.RESIDENCE_INFOS.ERRORS');

        if (!every(housingValidation, (validation) => validation)) {
            errors.push({
                code: 'error.housingOptions',
                message: LocaleService.t('FINANCING.RESIDENCE_INFOS.ERRORS.HOUSING_OPTIONS'),
            });
        }

        this.setState({
            errors,
            isSubmitted: true,
            isValid: !errors.length,
        });

        this.props.onDone(
            GroupResponseService.createResponse(!errors.length, callbackObject, null, null, 'residenceInformations')
        );
    }

    onBack(callbackObject) {
        this.props.onBack(
            GroupResponseService.createResponse(true, callbackObject, null, null, 'residenceInformations')
        );
    }

    onHousingTypeChanged(newValue) {
        const valueIsValid = newValue && newValue !== '';

        this.setState((oldState) => {
            const { formValidation } = oldState;

            formValidation.housingType = valueIsValid;

            const validationFields = {};
            each(residenceFormConfig[newValue.value], (input) => {
                validationFields[input.inputName] = !input.isRequired;
            });

            return {
                formValidation,
                housingType: newValue.value,
                housingValues: {},
                housingValidation: validationFields,
            };
        });
    }

    onEditAddressClick() {
        this.setState({ editModeCurrentAddress: true });
    }

    onDurationChange(newValue, inputName) {
        if (newValue === '' || parseInt(newValue, 10) < 0) {
            this.setState((oldState) => {
                const { inputs, formValidation } = oldState;
                formValidation[inputName] = false;
                inputs[inputName] = newValue === '' ? '' : 0;
                return { inputs, formValidation };
            });
            return;
        }

        const parsedValue = parseInt(newValue, 10);
        // eslint-disable-next-line no-restricted-globals
        const validatedValue = isFinite(newValue) ? parsedValue : 0;

        if (inputName === 'yearCount') {
            this.setState((oldState) => {
                const { inputs, formValidation } = oldState;
                inputs.yearCount = validatedValue;
                formValidation.yearCount = true;
                return { inputs, formValidation };
            });
        } else if (inputName === 'monthCount') {
            this.setState((oldState) => {
                const { inputs, formValidation } = oldState;
                let { yearCount } = inputs;
                let monthCount = validatedValue;

                if (validatedValue > 12) {
                    yearCount += Math.floor(validatedValue / 12);
                    monthCount = validatedValue % 12;
                }

                inputs.yearCount = yearCount;
                inputs.monthCount = monthCount;
                formValidation.yearCount = true;
                formValidation.monthCount = true;
                return { inputs, formValidation };
            });
        }
    }

    onHousingValueChange(newValue, inputName, isValid) {
        this.setState((oldState) => {
            const { housingValues, housingValidation } = oldState;

            housingValues[inputName] = newValue;
            housingValidation[inputName] = isValid;

            return { housingValues, housingValidation };
        });
    }

    onAddressChange(result) {
        this.setState((oldState) => {
            const { formValidation, inputs } = oldState;

            formValidation.currentAddress = result.valid;
            inputs.address = result.value || oldState.inputs.address;

            return {
                formValidation,
                inputs,
            };
        });
    }

    isAddressComplete(receivedAddress) {
        const address = receivedAddress || this.state.inputs.address;
        if (!address) {
            return false;
        }
        return !!address.address && !!address.postalCode && !!address.city && !!address.province;
    }

    render() {
        const { isOpen } = this.props;

        const {
            editModeCurrentAddress,
            errors,
            formValidation,
            housingType,
            housingValues,
            housingValidation,
            inputs,
            isSubmitted,
            isValid,
        } = this.state;

        const addressLabels = {
            address: LocaleService.t('FINANCING.RESIDENCE_INFOS.ADDRESS.ADDRESS'),
            address2: LocaleService.t('FINANCING.RESIDENCE_INFOS.ADDRESS.ADDRESS2'),
            city: LocaleService.t('FINANCING.RESIDENCE_INFOS.ADDRESS.CITY'),
            postalCode:
                this.country === 'us'
                    ? LocaleService.t('FINANCING.RESIDENCE_INFOS.ADDRESS.ZIP_CODE')
                    : LocaleService.t('FINANCING.RESIDENCE_INFOS.ADDRESS.POSTAL_CODE'),
            province:
                this.country === 'us'
                    ? LocaleService.t('FINANCING.RESIDENCE_INFOS.ADDRESS.STATE')
                    : LocaleService.t('FINANCING.RESIDENCE_INFOS.ADDRESS.PROVINCE'),
        };

        const callbackObject = {
            inputs,
            housingType: housingType || '',
            housingValues,
        };

        const currentAddressForm = (
            <div className="addressBlock clearfix">
                <Groups.AddressGroup
                    values={inputs.address}
                    labels={addressLabels}
                    onChange={this.onAddressChange}
                    provinces={this.provinces}
                    hasBeenSubmit={isSubmitted}
                    isRestrictUpdateInfo
                    country={this.country}
                />
            </div>
        );

        let housingFormBlock = null;
        if (housingType) {
            housingFormBlock = residenceFormConfig[housingType].map((input) => (
                <FormBuilder
                    key={input.inputName}
                    inputParams={{ ...input, value: housingValues[input.inputName] }}
                    onChange={this.onHousingValueChange}
                    hasBeenSubmit={isSubmitted}
                    isValid={housingValidation[input.inputName]}
                    errorLabel={input.errorTransKey}
                />
            ));
        }

        const residenceForm = (
            <div className="clearfix">
                <h4 className="sr-text is-bold">
                    {this.props.isCoApplicant
                        ? LocaleService.t('FINANCING.RESIDENCE_INFOS.CURRENT_ADDRESS')
                        : LocaleService.t('FINANCING.RESIDENCE_INFOS.MY_CURRENT_ADDRESS')}
                </h4>
                <br />
                {!editModeCurrentAddress ? (
                    <div>
                        <AddressDisplay {...inputs.address} />
                        <button className="sr-link" onClick={this.onEditAddressClick}>
                            {LocaleService.t('FINANCING.RESIDENCE_INFOS.EDIT_CURRENT_ADDRESS')} &gt;
                        </button>
                    </div>
                ) : (
                    currentAddressForm
                )}
                <div className="clearfix">
                    <InputText
                        label={LocaleService.t('FINANCING.RESIDENCE_INFOS.YEAR_COUNT')}
                        errorLabel={LocaleService.t('COMMONS_FORMS.ERRORS.RESIDENCE_INFOS.YEAR_COUNT')}
                        name="yearCount"
                        type="number"
                        value={inputs.yearCount}
                        classNames={['styled-placeholder', 'quarter']}
                        isValid={formValidation.yearCount}
                        isRequired
                        valueChange={this.onDurationChange}
                        hasBeenSubmit={isSubmitted}
                        maxLength={FIELD_LENGTH.DEFAULT_NUMBER_MAX}
                    />
                    <InputText
                        label={LocaleService.t('FINANCING.RESIDENCE_INFOS.MONTH_COUNT')}
                        errorLabel={LocaleService.t('COMMONS_FORMS.ERRORS.RESIDENCE_INFOS.MONTH_COUNT')}
                        name="monthCount"
                        type="number"
                        value={inputs.monthCount}
                        classNames={['styled-placeholder', 'quarter']}
                        isValid={formValidation.monthCount}
                        isRequired
                        valueChange={this.onDurationChange}
                        hasBeenSubmit={isSubmitted}
                        maxLength={FIELD_LENGTH.DEFAULT_NUMBER_MAX}
                    />
                </div>
                <div className="clearfix">
                    <h4 className="sr-text is-bold">{LocaleService.t('FINANCING.RESIDENCE_INFOS.HOUSING')}</h4>
                    <br />
                    <InputDropDown
                        name="homeStatus"
                        options={this.homeStatuses}
                        value={housingType}
                        valueChange={this.onHousingTypeChanged}
                        placeholder={LocaleService.t('FINANCING.RESIDENCE_INFOS.HOME_STATUS')}
                        errorLabel={LocaleService.t('COMMONS_FORMS.ERRORS.RESIDENCE_INFOS.HOME_STATUS')}
                        classNames={['half']}
                        isValid={formValidation.housingType}
                        hasBeenSubmit={isSubmitted}
                        isRequired
                    />
                    {housingFormBlock}
                </div>
                {errors.length !== 0 &&
                    isSubmitted &&
                    isOpen && (
                        <p className="financing__content-errors-message">
                            {LocaleService.t('COMMONS_FORMS.ERRORS.TITLE')}
                        </p>
                    )}
                <ButtonBlock onBack={() => this.onBack(callbackObject)} onDone={() => this.onDone(callbackObject)} />
            </div>
        );

        const formClass = isOpen
            ? 'financingBlockForm residenceForm is-open'
            : `financingBlockForm residenceForm is-closed ${isValid ? 'is-valid' : ''}`;

        return (
            <div className={formClass}>
                <icon className="icon-Crochet is-util" />
                {errors.length !== 0 && isSubmitted && isOpen && <ErrorsHandler errors={errors} />}
                <h3 className="sr-text is-bold is-util">
                    {this.props.isCoApplicant
                        ? LocaleService.t('FINANCING.RESIDENCE_INFOS.TELL_US_MORE_COAPPLICANT')
                        : LocaleService.t('FINANCING.RESIDENCE_INFOS.TELL_US_MORE')}
                </h3>
                <br />
                {isOpen ? residenceForm : <AddressDisplay {...inputs.address} />}
            </div>
        );
    }
}

ResidenceInfosForm.defaultProps = {
    isOpen: true,
    isCoApplicant: false,
};

ResidenceInfosForm.propTypes = {
    isOpen: PropTypes.bool,
    isCoApplicant: PropTypes.bool,
    residenceInformations: PropTypes.shape({
        address: PropTypes.shape({
            address: PropTypes.string.isRequired,
            address2: PropTypes.string.isRequired,
            postalCode: PropTypes.string.isRequired,
            city: PropTypes.string.isRequired,
            province: PropTypes.string.isRequired,
        }),
        housingType: PropTypes.string,
        housingValues: PropTypes.object,
    }).isRequired,
    onDone: PropTypes.func.isRequired,
    onBack: PropTypes.func.isRequired,
    country: PropTypes.string.isRequired,
};

ResidenceInfosForm.displayName = 'ResidenceInfosForm';

export default ResidenceInfosForm;
